import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты для сравнения.
          Выполните расчет и нажмите кнопку "Сохранить расчет", чтобы добавить его в эту панель.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {/* Для расчета расхода */}
              {calc.calculationType === 'flowRate' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Диаметр:</span>
                    <span className="font-medium">{formatNumber(calc.formData.pipeInnerDiameter, 1)} мм</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Перепад давления:</span>
                    <span>{formatNumber(calc.formData.pressureDrop, 2)} бар</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Расход воды:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {formatNumber(calc.results.flowRate, 2)} м³/ч
                    </span>
                  </div>
                </>
              )}
              
              {/* Для расчета перепада давления */}
              {calc.calculationType === 'pressure' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Диаметр:</span>
                    <span className="font-medium">{formatNumber(calc.formData.pipeInnerDiameter, 1)} мм</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Расход:</span>
                    <span>{formatNumber(calc.formData.flowRate, 2)} м³/ч</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Перепад давления:</span>
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      {formatNumber(calc.results.pressureDrop, 3)} бар
                    </span>
                  </div>
                </>
              )}
              
              {/* Для расчета диаметра */}
              {calc.calculationType === 'diameter' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Расход:</span>
                    <span>{formatNumber(calc.formData.requiredFlowRate, 2)} м³/ч</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Макс. перепад:</span>
                    <span>{formatNumber(calc.formData.maxPressureDrop, 2)} бар</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Опт. диаметр:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {calc.results.standardRecommendedDiameter 
                        ? formatNumber(calc.results.standardRecommendedDiameter, 1) 
                        : formatNumber(calc.results.diameter * 1000, 1)} мм
                    </span>
                  </div>
                </>
              )}
              
              <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-600 flex justify-between text-xs">
                <span>{getPipeTypeName(calc.formData.pipeType)}</span>
                <span>L = {formatNumber(calc.formData.pipeLength, 0)} м</span>
                <span>{calc.formData.waterTemperature}°C</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedCalculations.length > 1 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <p className="font-medium">Сравнительный анализ:</p>
            <ul className="mt-1 list-disc pl-4 space-y-1">
              {getSimilarParameters(savedCalculations).map((param, index) => (
                <li key={index}>{param}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для получения названия типа трубы
const getPipeTypeName = (pipeTypeId) => {
  const pipeTypeMap = {
    'steel': 'Стальная',
    'copper': 'Медная',
    'pvc': 'ПВХ (пластиковая)',
    'pp': 'Полипропиленовая',
    'pex': 'Сшитый полиэтилен (PEX)',
    'hdpe': 'ПНД (полиэтилен низкого давления)',
    'cast_iron': 'Чугунная',
    'stainless_steel': 'Нержавеющая сталь'
  };
  
  return pipeTypeMap[pipeTypeId] || pipeTypeId;
};

// Вспомогательная функция для получения общих и различных параметров
const getSimilarParameters = (calculations) => {
  if (calculations.length < 2) return [];
  
  // Найдем минимальное и максимальное значение для диаметра и расхода
  const diameters = calculations.map(c => parseFloat(c.formData.pipeInnerDiameter)).filter(d => !isNaN(d));
  const flowRates = calculations
    .map(c => {
      if (c.calculationType === 'flowRate') return c.results.flowRate;
      if (c.calculationType === 'pressure') return parseFloat(c.formData.flowRate);
      if (c.calculationType === 'diameter') return parseFloat(c.formData.requiredFlowRate);
      return NaN;
    })
    .filter(f => !isNaN(f));
  
  const minDiameter = Math.min(...diameters);
  const maxDiameter = Math.max(...diameters);
  const minFlowRate = Math.min(...flowRates);
  const maxFlowRate = Math.max(...flowRates);
  
  const insights = [];
  
  // Разница в диаметрах
  if (minDiameter !== maxDiameter) {
    const diameterRatio = (maxDiameter / minDiameter).toFixed(1);
    insights.push(`Диаметр: разница в ${diameterRatio}x раз (${minDiameter} → ${maxDiameter} мм)`);
  }
  
  // Разница в расходах
  if (minFlowRate !== maxFlowRate) {
    const flowRateRatio = (maxFlowRate / minFlowRate).toFixed(1);
    insights.push(`Расход: разница в ${flowRateRatio}x раз (${minFlowRate.toFixed(1)} → ${maxFlowRate.toFixed(1)} м³/ч)`);
  }
  
  // Разные материалы
  const uniquePipeTypes = [...new Set(calculations.map(c => c.formData.pipeType))];
  if (uniquePipeTypes.length > 1) {
    insights.push(`Разные материалы труб: ${uniquePipeTypes.map(getPipeTypeName).join(', ')}`);
  }
  
  // Если длина труб одинаковая
  const pipeLengths = calculations.map(c => parseFloat(c.formData.pipeLength));
  if (new Set(pipeLengths).size === 1) {
    insights.push(`Одинаковая длина труб: ${pipeLengths[0]} м`);
  }
  
  return insights.length ? insights : ['Анализируйте разные варианты расчетов для сравнения параметров'];
};

export default ComparisonSidebar;